/**
 * Grid Snapping Module
 *
 * Provides utilities for snapping positions to a grid.
 */

import type { Point, EditorNode } from './types';

/**
 * Snap a single value to the nearest grid line.
 */
export function snapToGrid(value: number, gridSize: number): number {
	return Math.round(value / gridSize) * gridSize;
}

/**
 * Snap a point to the nearest grid intersection.
 */
export function snapPointToGrid(point: Point, gridSize: number): Point {
	return {
		x: snapToGrid(point.x, gridSize),
		y: snapToGrid(point.y, gridSize),
	};
}

/**
 * Snap an offset so that the first node ends up on a grid line.
 * This keeps multi-node moves aligned relative to the first node's grid position.
 */
export function snapOffsetToGrid(
	nodeIds: string[],
	nodes: EditorNode[],
	offset: Point,
	gridSize: number,
): Point {
	if (nodeIds.length === 0) return offset;

	const firstNode = nodes.find((n) => n.id === nodeIds[0]);
	if (!firstNode) return offset;

	// Calculate where the first node would be with the current offset
	const targetPosition = {
		x: firstNode.position.x + offset.x,
		y: firstNode.position.y + offset.y,
	};

	// Snap the target position to grid
	const snappedPosition = snapPointToGrid(targetPosition, gridSize);

	// Return the offset that achieves the snapped position
	return {
		x: snappedPosition.x - firstNode.position.x,
		y: snappedPosition.y - firstNode.position.y,
	};
}
